// Passkeys: logging in with one (with the login page's button, or by picking one from the username field's
// autofill) and adding one on the Edit profile page. The browser's WebAuthn API does the work; the server side is
// PasskeySessionsController and PasskeysController, and they trade options and results as JSON, with the binary
// parts (challenges, ids, signatures) encoded as base64url text.

function toBytes(base64url) {
  var base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  var binary = atob(base64 + '==='.slice((base64.length + 3) % 4))
  return Uint8Array.from(binary, function(c) { return c.charCodeAt(0) })
}

function toBase64url(buffer) {
  var binary = String.fromCharCode.apply(null, new Uint8Array(buffer))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function withBinaryIds(list) {
  return (list || []).map(function(item) { return Object.assign({}, item, { id: toBytes(item.id) }) })
}

function creationOptions(json) {
  return Object.assign({}, json, {
    challenge: toBytes(json.challenge),
    user: Object.assign({}, json.user, { id: toBytes(json.user.id) }),
    excludeCredentials: withBinaryIds(json.excludeCredentials)
  })
}

function requestOptions(json) {
  return Object.assign({}, json, { challenge: toBytes(json.challenge), allowCredentials: withBinaryIds(json.allowCredentials) })
}

function credentialJSON(credential) {
  var response = credential.response
  var json = {
    type: credential.type,
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: { clientDataJSON: toBase64url(response.clientDataJSON) }
  }
  if (response.attestationObject) { // a new passkey
    json.response.attestationObject = toBase64url(response.attestationObject)
    json.response.transports = response.getTransports ? response.getTransports() : []
  } else { // a log-in
    json.response.authenticatorData = toBase64url(response.authenticatorData)
    json.response.signature = toBase64url(response.signature)
    json.response.userHandle = response.userHandle ? toBase64url(response.userHandle) : null
  }
  return json
}

function postJSON(url, body) {
  var token = document.querySelector('meta[name="csrf-token"]')
  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': token && token.content },
    body: JSON.stringify(body)
  }).then(function(response) {
    if (response.status === 429) { throw new Error('Too many tries. Please wait a moment and try again.') }
    return response.json().catch(function() { return {} }).then(function(json) {
      if (!response.ok) {
        var error = new Error(json.error || 'Something went wrong. Please try again.')
        error.response = json
        throw error
      }
      return json
    })
  })
}

// Tell the person's password manager what's current here (where it supports being told, through the WebAuthn Signal
// API), so it can drop passkeys removed on the site and show their current username. Waits a moment at most.
function sendSignals(signals) {
  if (!signals || !window.PublicKeyCredential) { return Promise.resolve() }
  var calls = []
  if (PublicKeyCredential.signalAllAcceptedCredentials) {
    calls.push(PublicKeyCredential.signalAllAcceptedCredentials({
      rpId: signals.rpId, userId: signals.userId, allAcceptedCredentialIds: signals.allAcceptedCredentialIds
    }))
  }
  if (PublicKeyCredential.signalCurrentUserDetails) {
    calls.push(PublicKeyCredential.signalCurrentUserDetails({
      rpId: signals.rpId, userId: signals.userId, name: signals.name, displayName: signals.displayName
    }))
  }
  var sent = Promise.all(calls.map(function(call) { return call.catch(function() {}) }))
  return Promise.race([sent, new Promise(function(resolve) { setTimeout(resolve, 1000) })])
}

function showError(element, message) {
  element.textContent = message || ''
  element.hidden = !message
}

// The login page. While it's open, the browser is asked (quietly) for a passkey to offer in the username field's
// autofill; the button asks outright, which replaces the quiet request until it's done.
function setUpSignIn(box) {
  var form = box.closest('form')
  var button = box.querySelector('[data-passkey-sign-in-button]')
  var error = box.querySelector('[data-passkey-error]')
  var pending = null
  box.hidden = false
  // The password's Log in button becomes the second choice
  form.querySelector('[data-password-sign-in-button]').classList.add('secondary')

  function signIn(conditional) {
    if (pending) { pending.abort() }
    var controller = pending = new AbortController()
    var picked = null, rpId
    if (!conditional) { // the quiet request restarts right after a failure; leave its message up
      showError(error, null)
      box.querySelector('[data-passkey-hint]').hidden = true
    }
    return postJSON(box.dataset.optionsUrl, {}).then(function(options) {
      rpId = options.rpId
      var request = { publicKey: requestOptions(options), signal: controller.signal }
      if (conditional) { request.mediation = 'conditional' }
      return navigator.credentials.get(request)
    }).then(function(credential) {
      picked = credential
      return postJSON(box.dataset.url, { credential: credentialJSON(credential) })
    }).then(function(result) {
      return sendSignals(result.signals).then(function() { window.location.assign(result.redirect_to) })
    }).catch(function(e) {
      // A passkey removed on the site: so it isn't offered again, ask the password manager to forget it
      if (e.response && e.response.unknown_credential && PublicKeyCredential.signalUnknownCredential) {
        PublicKeyCredential.signalUnknownCredential({ rpId: rpId, credentialId: picked.id }).catch(function() {})
      }
      if (controller.signal.aborted) { return } // replaced by a newer request
      // NotAllowedError is the browser's answer when the person closes its passkey prompt, so that needs no message.
      // Nor does the quiet request for the autofill failing before anyone picked a passkey: they didn't ask for it.
      if (e.name !== 'NotAllowedError' && (!conditional || picked)) { showError(error, e.message) }
      // Closing the prompt from the button likely means there's no passkey to pick: point to the other ways in
      if (e.name === 'NotAllowedError' && !conditional) { box.querySelector('[data-passkey-hint]').hidden = false }
      // Keep offering passkeys in the autofill, unless that's what just failed before one was even picked
      if (!conditional || picked) { offerInAutofill() }
    })
  }

  function offerInAutofill() {
    if (!PublicKeyCredential.isConditionalMediationAvailable) { return }
    PublicKeyCredential.isConditionalMediationAvailable().then(function(available) {
      if (available) { signIn(true) }
    })
  }

  button.addEventListener('click', function() { signIn(false) })
  offerInAutofill()
}

// The Edit profile page's "Add a passkey" form
function setUpAdd(form) {
  var error = form.querySelector('[data-passkey-error]')
  var button = form.querySelector('button')
  form.hidden = false

  form.addEventListener('submit', function(e) {
    e.preventDefault()
    showError(error, null)
    button.disabled = true
    var password = form.elements.current_password // left out if they logged in with it a few minutes ago
    postJSON(form.dataset.optionsUrl, { current_password: password ? password.value : null }).then(function(options) {
      return navigator.credentials.create({ publicKey: creationOptions(options) })
    }).then(function(credential) {
      return postJSON(form.dataset.url, { credential: credentialJSON(credential), nickname: form.elements.nickname.value })
    }).then(function(result) {
      window.location.assign(result.redirect_to)
    }).catch(function(e) {
      button.disabled = false
      if (e.name === 'InvalidStateError') { // the browser found one of this account's passkeys already there
        showError(error, 'There\'s already a passkey for your account there, so you\'re all set.')
      } else if (e.name !== 'NotAllowedError') { // closing the browser's prompt needs no message
        showError(error, e.message)
      }
    })
  })
}

// The suggestion, after a password log-in, to add a passkey. Browsers that can turn a saved password into a passkey
// (conditional create) are first asked to, quietly; if they do, the suggestion becomes a note saying so. Otherwise
// it shows, also if the browser hasn't answered within a few seconds.
function setUpOffer(box) {
  var ask = box.querySelector('[data-passkey-offer-ask]')
  var saved = box.querySelector('[data-passkey-offer-saved]')

  box.querySelector('[data-passkey-offer-dismiss]').addEventListener('click', function(e) {
    e.preventDefault()
    box.hidden = true
    postJSON(box.dataset.dismissUrl, {}).catch(function() {})
  })

  var shown = setTimeout(function() { box.hidden = false }, 3000)
  addAutomatically(box).then(function() {
    clearTimeout(shown)
    ask.hidden = true
    saved.hidden = false
    box.classList.replace('secondary', 'success')
    box.hidden = false
  }, function() {
    clearTimeout(shown)
    box.hidden = false
  })
}

function addAutomatically(box) {
  var capabilities = PublicKeyCredential.getClientCapabilities ? PublicKeyCredential.getClientCapabilities() : Promise.resolve({})
  return capabilities.then(function(available) {
    if (!available.conditionalCreate) { throw new Error('unavailable') }
    return postJSON(box.dataset.optionsUrl, { automatic: true })
  }).then(function(options) {
    return navigator.credentials.create({ publicKey: creationOptions(options), mediation: 'conditional' })
  }).then(function(credential) {
    return postJSON(box.dataset.url, { credential: credentialJSON(credential), automatic: true })
  })
}

// The sign-up page: "How do you want to log in?" With a passkey (the default where browsers support them), Sign up
// takes the account details from the form and makes the account with a passkey instead of submitting the form; with
// a password, the password fields show and the form submits as usual.
function setUpSignUp(box) {
  var form = box.closest('form')
  var button = form.querySelector('input[type="submit"]')
  var note = form.querySelector('[data-passkey-sign-up-note]')
  var error = form.querySelector('[data-passkey-error]')
  var passwordPart = form.querySelector('[data-password-sign-up]')
  var choices = form.querySelectorAll('input[name="login_method"]')
  box.hidden = false

  function withPasskey() { return form.querySelector('input[name="login_method"]:checked').value === 'passkey' }
  function update() {
    passwordPart.hidden = withPasskey()
    note.hidden = !withPasskey()
    showError(error, null)
  }
  choices.forEach(function(choice) { choice.addEventListener('change', update) })
  update()

  form.addEventListener('submit', function(e) {
    if (!withPasskey()) { return } // the usual password sign-up
    e.preventDefault()
    showError(error, null)
    button.disabled = true
    var user = {
      username: form.elements['user[username]'].value,
      email: form.elements['user[email]'].value,
      private_collections: form.querySelector('input[type="checkbox"][name="user[private_collections]"]').checked
    }
    postJSON(box.dataset.optionsUrl, { user: user }).then(function(options) {
      return navigator.credentials.create({ publicKey: creationOptions(options) })
    }).then(function(credential) {
      return postJSON(box.dataset.url, { credential: credentialJSON(credential) })
    }).then(function(result) {
      return sendSignals(result.signals).then(function() { window.location.assign(result.redirect_to) })
    }).catch(function(e) {
      button.disabled = false
      if (e.name !== 'NotAllowedError') { showError(error, e.message) } // closing the browser's prompt needs no message
    })
  })
}

// "Confirm it's you with your passkey", for accounts without a password
function setUpConfirm(box) {
  var button = box.querySelector('[data-passkey-confirm-button]')
  var error = box.querySelector('[data-passkey-error]')

  button.addEventListener('click', function() {
    showError(error, null)
    button.disabled = true
    postJSON(box.dataset.optionsUrl, {}).then(function(options) {
      return navigator.credentials.get({ publicKey: requestOptions(options) })
    }).then(function(credential) {
      return postJSON(box.dataset.url, { credential: credentialJSON(credential) })
    }).then(function() {
      if ('reload' in box.dataset) { window.location.reload(); return }
      button.hidden = true
      box.querySelector('[data-passkey-confirmed]').hidden = false
    }).catch(function(e) {
      button.disabled = false
      if (e.name !== 'NotAllowedError') { showError(error, e.message) }
    })
  })
}

document.addEventListener('DOMContentLoaded', function() {
  if (!window.PublicKeyCredential) { // too old a browser for passkeys: leave everything hidden but the note saying so
    document.querySelectorAll('[data-passkeys-unsupported]').forEach(function(note) { note.hidden = false })
    return
  }
  document.querySelectorAll('[data-passkey-sign-in]').forEach(setUpSignIn)
  document.querySelectorAll('[data-passkey-add]').forEach(setUpAdd)
  document.querySelectorAll('[data-passkey-offer]').forEach(setUpOffer)
  document.querySelectorAll('[data-passkey-sign-up]').forEach(setUpSignUp)
  document.querySelectorAll('[data-passkey-confirm]').forEach(setUpConfirm)
  document.querySelectorAll('[data-passkey-signals]').forEach(function(element) {
    sendSignals(JSON.parse(element.dataset.passkeySignals))
  })
})
